import type { Interpolation, Theme } from '@emotion/react';
import { css, cx } from '@linaria/atomic';
import { theme } from '@frontend/theme';
import { emptyStateGraphics, Text, PrimaryButton, TextProps } from '@frontend/design-system';

export type EmptyStateConfig = {
  type?: keyof typeof emptyStateGraphics;
  header?: string;
  description?: string | React.ReactNode;
  action?: { label: string } & Omit<React.ComponentProps<typeof PrimaryButton>, 'children' | 'size'>;
  disableGraphic?: boolean;
  textProps?: Partial<TextProps>;
  /** @deprecated Emotion is being removed. Pass a linaria class via `className` instead. */
  styles?: Interpolation<Theme>;
  className?: string;
};

export type EmptyStateProps = {
  emptyStateConfig?: EmptyStateConfig;
};

export const EmptyStates = ({ emptyStateConfig }: EmptyStateProps) => {
  const { label, ...rest } = emptyStateConfig?.action || {};
  const EmptyStateGraphic = emptyStateConfig?.type
    ? emptyStateGraphics[emptyStateConfig?.type]
    : emptyStateGraphics['generic'];
  return (
    <div>
      <div className={cx(containerStyle, emptyStateConfig?.className)} css={emptyStateConfig?.styles}>
        {!emptyStateConfig?.disableGraphic && <EmptyStateGraphic className={graphicStyle} />}
        <div className={emptyStateConfig?.disableGraphic ? contentNoGraphicStyle : contentStyle}>
          <Text className={textStyle} textAlign='center' color='light' {...emptyStateConfig?.textProps}>
            {emptyStateConfig?.header ?? 'No data to display'}
          </Text>
        </div>
        {(emptyStateConfig?.description || emptyStateConfig?.action) && (
          <div>
            {!!emptyStateConfig?.description && (
              <Text className={textStyle} textAlign='center' color='light' {...emptyStateConfig?.textProps}>
                {emptyStateConfig?.description}
              </Text>
            )}
          </div>
        )}
        {!!emptyStateConfig?.action && (
          <PrimaryButton className={actionStyle} size='tiny' {...rest}>
            {label}
          </PrimaryButton>
        )}
      </div>
    </div>
  );
};

const containerStyle = css`
  position: sticky;
  left: 0;
  display: flex;
  align-items: center;
  padding: ${theme.spacing(5)} 0;
  flex-direction: column;
`;

const graphicStyle = css`
  height: 200px;
`;

const contentStyle = css`
  margin-top: ${theme.spacing(3)};
`;

const contentNoGraphicStyle = css`
  margin-top: 0;
`;

const textStyle = css`
  font-size: ${theme.fontSize(16)};
  max-width: 400px;
`;

const actionStyle = css`
  margin-top: ${theme.spacing(3)};
  margin-bottom: ${theme.spacing(1)};
`;
